import { action, makeAutoObservable } from "mobx";
import { ReactElement } from "react";

import { TMessage } from "../i18n/tt";

export type AlertMessageText = TMessage | (() => ReactElement);
export type AlertMessageParams = { autoHide?: boolean | number };
export type AlertMessageType = "error" | "warning" | "success" | "info";
export type AlertMessage = {
  type: AlertMessageType;
  message: AlertMessageText;
  params?: AlertMessageParams;
};

function getAutoHideDuration(autoHide: boolean | number) {
  return typeof autoHide === "number" ? autoHide : 3000;
}

export default class AlertMessageStore {
  type: AlertMessageType = "info";
  message?: AlertMessageText;
  autoHide?: boolean | number;
  autoHideTimeoutId?: ReturnType<typeof setTimeout>;
  messageVisible = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  showMessage(type: AlertMessageType, message: AlertMessageText, params?: AlertMessageParams) {
    this.type = type;
    this.message = message;
    this.autoHide = params?.autoHide ?? (type === "info" || type === "success");

    clearTimeout(this.autoHideTimeoutId);
    this.autoHideTimeoutId = undefined;
    if (this.autoHide) {
      this.autoHideTimeoutId = setTimeout(() => {
        this.hideMessage();
      }, getAutoHideDuration(this.autoHide));
    }

    setTimeout(
      action(() => {
        this.messageVisible = true;
      }),
    );
  }

  public showGeneralError(params?: AlertMessageParams) {
    this.showError({ key: "general-error" }, params ?? { autoHide: true });
  }

  public showGeneralSuccess(params?: AlertMessageParams) {
    this.showSuccess({ key: "general-success" }, params ?? { autoHide: true });
  }

  public showError(message: AlertMessageText, params?: AlertMessageParams) {
    this.showMessage("error", message, params);
  }

  public showWarning(message: AlertMessageText, params?: AlertMessageParams) {
    this.showMessage("warning", message, params);
  }

  public showSuccess(message: AlertMessageText, params?: AlertMessageParams) {
    this.showMessage("success", message, {
      ...params,
      autoHide: params?.autoHide ?? true,
    });
  }

  public showInfo(message: AlertMessageText, params?: AlertMessageParams) {
    this.showMessage("info", message, {
      ...params,
      autoHide: params?.autoHide ?? true,
    });
  }

  public hideMessage() {
    this.messageVisible = false;
  }
}
